/**
 * "In at least N lists" (task 2026-09-24-combine-more-filters) — the page's
 * state for the Filters card's list rule, and what it sends.
 *
 * `"all"` is "Only people in every list": it stays "every list" when the
 * recruiter adds or removes a list, exactly as the older boolean did, and
 * is sent as `require_all_sources`. A number is "at least N": clamped to
 * the lists selected now, so removing a list can never send an N above the
 * source count (the API would 422 it), and sent as `min_sources` — or as
 * `require_all_sources` when the clamp reaches every list. `null` is no
 * rule. With fewer than two lists nothing is sent (bug hunt 2026-09-23, F7:
 * the rule means nothing with one list, and the server ignores it too).
 */

export type ListRule = "all" | number | null;

/** The lowest N the stepper offers — with N = 1 every person qualifies. */
export const MIN_LIST_RULE = 2;

export interface AppliedListRule {
  /** The N the rule keeps people at (`null` = no rule in effect). */
  n: number | null;
  /** Sent as `CombineRecipe.require_all_sources`. */
  requireAllSources: boolean;
  /** Sent as `CombineRecipe.min_sources` (`null` when `requireAllSources`). */
  minSources: number | null;
}

export function applyListRule(rule: ListRule, sourceCount: number): AppliedListRule {
  if (rule === null || sourceCount < MIN_LIST_RULE) {
    return { n: null, requireAllSources: false, minSources: null };
  }
  const n = rule === "all" ? sourceCount : Math.min(Math.max(rule, MIN_LIST_RULE), sourceCount);
  return n >= sourceCount
    ? { n: sourceCount, requireAllSources: true, minSources: null }
    : { n, requireAllSources: false, minSources: n };
}

/** One step of the stepper: `+1` reaching the source count becomes `"all"`;
 *  `-1` from `"all"` is `count - 1`, never below `MIN_LIST_RULE`. */
export function stepListRule(rule: ListRule, delta: 1 | -1, sourceCount: number): ListRule {
  const current = applyListRule(rule ?? "all", sourceCount).n ?? sourceCount;
  const next = Math.min(Math.max(current + delta, MIN_LIST_RULE), sourceCount);
  return next >= sourceCount ? "all" : next;
}
